// Nutzer, Rollen und Sicherheit (Runde 8, Agent 8C): Typen und Client nach crm/API.md, Abschnitt 23.3. Nutzer und Rollen brauchen das
// Recht `nutzer_verwalten`; die Zwei-Faktor-Routen für das eigene Konto nur die Anmeldung. 8C erweitert diese Datei nur additiv.
import { http } from "./client";

export interface VerwaltungsNutzer {
  id: string;
  name: string;
  email: string | null;
  telefon: string | null;
  rolle: string;
  rolle_name?: string | null;
  aktiv: boolean;
  deaktiviert_at: string | null;
  zwei_faktor: boolean;
  letzte_anmeldung: string | null;
  close_schluessel: boolean;
  /** womit er sich anmeldet: eigenes Passwort, Zugang aus dem Mitarbeiterportal oder gar nicht */
  anmeldung?: "eigen" | "portal" | "keine";
  /** aus Close importiert oder im CRM angelegt (Einladung) */
  quelle?: "close" | "crm";
}

export interface Einladung {
  id: string;
  email: string;
  rolle: string;
  rolle_name?: string | null;
  eingeladen_von_name?: string | null;
  gueltig_bis: string;
  date_created: string | null;
  /** nur direkt nach dem Anlegen: der Link zum Weitergeben (es geht keine Mail automatisch raus) */
  link?: string;
}

export interface UmhaengenVorschau {
  aufgaben: number;
  opportunities: number;
  nummern: number;
  smart_views: number;
  offene_anrufe: number;
  /** Close-Systemaufgaben (verpasste Anrufe usw.): lassen sich nicht umhängen */
  systemaufgaben?: number;
}

export interface DeaktivierenErgebnis {
  nutzer: VerwaltungsNutzer;
  umgehaengt: { aufgaben: number; opportunities: number; nummern: number };
  systemaufgaben_geblieben: number;
}

export type LeadSichtbarkeit = { modus: "alle" | "eigene" | "felder"; felder: string[] };

export interface Rolle {
  schluessel: string;
  name: string;
  basis: "admin" | "superuser" | "user" | "restricted";
  /** vollständige Liste der Rechte der Rolle */
  rechte: string[];
  lead_sichtbarkeit: LeadSichtbarkeit;
  ist_system: boolean;
  nutzer: number;
}

export interface RechteEintrag {
  recht: string;
  titel: string;
  gruppe: string;
  beschreibung?: string | null;
}

export interface ZweiFaktorStand {
  aktiv: boolean;
  seit: string | null;
  codes_uebrig: number;
  erzwungen: boolean;
  /** Geheimnis erzeugt, erster Code noch nicht bestätigt */
  eingerichtet_wartet?: boolean;
}

export interface SicherheitsEinstellungen {
  zwei_faktor_erzwingen: boolean;
  aktive_nutzer?: number;
  mit_zwei_faktor?: number;
}

const k = encodeURIComponent;

export const nutzerVerwaltungApi = {
  nutzer: () => http.get<{ data: VerwaltungsNutzer[] }>("/api/nutzer"),
  einladungen: () => http.get<{ data: Einladung[] }>("/api/nutzer/einladungen"),
  einladen: (email: string, rolle = "user") => http.post<{ einladung: Einladung }>("/api/nutzer/einladungen", { email, rolle }),
  einladungWiderrufen: (id: string) => http.del<void>(`/api/nutzer/einladungen/${k(id)}`),
  /** ohne Anmeldung */
  einladungAnsehen: (token: string) => http.get<{ email: string; rolle: string; rolle_name?: string }>(`/api/nutzer/einladung/${k(token)}`),
  einladungAnnehmen: (e: { token: string; vorname: string; nachname: string; passwort: string }) => http.post<{ ok: boolean }>("/api/nutzer/einladung/annehmen", e),
  aendern: (id: string, e: { rolle?: string; vorname?: string; nachname?: string; telefon?: string }) => http.patch<{ nutzer: VerwaltungsNutzer }>(`/api/nutzer/${k(id)}`, e),
  umhaengenVorschau: (id: string) => http.get<UmhaengenVorschau>(`/api/nutzer/${k(id)}/umhaengen-vorschau`),
  deaktivieren: (id: string, umhaengenAn?: string) => http.post<DeaktivierenErgebnis>(`/api/nutzer/${k(id)}/deaktivieren`, { umhaengen_an: umhaengenAn ?? null }),
  aktivieren: (id: string) => http.post<{ nutzer: VerwaltungsNutzer }>(`/api/nutzer/${k(id)}/aktivieren`),
  zweiFaktorZuruecksetzen: (id: string) => http.post<{ nutzer: VerwaltungsNutzer }>(`/api/nutzer/${k(id)}/2fa-zuruecksetzen`),
  rollen: () => http.get<{ data: Rolle[] }>("/api/rollen"),
  rechte: () => http.get<{ data: RechteEintrag[] }>("/api/rollen/rechte"),
  rolleAnlegen: (e: { name: string; basis?: Rolle["basis"]; rechte?: string[]; lead_sichtbarkeit?: LeadSichtbarkeit }) => http.post<{ rolle: Rolle }>("/api/rollen", e),
  rolleAendern: (schluessel: string, e: { name?: string; rechte?: string[]; lead_sichtbarkeit?: LeadSichtbarkeit }) => http.patch<{ rolle: Rolle }>(`/api/rollen/${k(schluessel)}`, e),
  rolleLoeschen: (schluessel: string, ersatzRolle?: string) => http.del<void>(`/api/rollen/${k(schluessel)}${ersatzRolle ? `?ersatz_rolle=${k(ersatzRolle)}` : ""}`),
  rolleDuplizieren: (schluessel: string) => http.post<{ rolle: Rolle }>(`/api/rollen/${k(schluessel)}/duplizieren`),
  zweiFaktor: () => http.get<ZweiFaktorStand>("/api/sicherheit/2fa"),
  zweiFaktorEinrichten: () => http.post<{ geheimnis: string; otpauth_url: string }>("/api/sicherheit/2fa/einrichten"),
  zweiFaktorBestaetigen: (code: string) => http.post<{ wiederherstellungscodes: string[] }>("/api/sicherheit/2fa/bestaetigen", { code }),
  zweiFaktorAbschalten: (code: string, passwort: string) => http.post<void>("/api/sicherheit/2fa/abschalten", { code, passwort }),
  zweiFaktorNeueCodes: (code: string, passwort: string) =>
    http.post<{ wiederherstellungscodes: string[] }>("/api/sicherheit/2fa/wiederherstellungscodes", { code, passwort }),
  sicherheit: () => http.get<SicherheitsEinstellungen>("/api/sicherheit/einstellungen"),
  sicherheitSetzen: (e: { zwei_faktor_erzwingen?: boolean }) => http.put<SicherheitsEinstellungen>("/api/sicherheit/einstellungen", e),
};
